import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Alert,
  Divider,
  Autocomplete,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { csAdminApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { canCreateParentCompany, isCompanySecretaryRole } from '../../constants/csAdminRoles';
import { useGlobalPermission } from '../../hooks/useGlobalPermission';
import { mapCompanyFromApi, mapLegacyGovernanceCode } from '../../utils/apiMappers';
import {
  parseShareholdingPercent,
  shareholdingPercentInputProps,
} from '../../utils/shareholdingPercent';
import { useLookups, suggestGovernanceType, filterCompanyTypesByStructure } from '../../hooks/useLookups';

const parseDescriptionFields = (description) => {
  if (!description) return { address: '', city: '', poBox: '' };
  const parts = String(description).split(',').map((part) => part.trim());
  return {
    address: parts[0] || '',
    city: parts[1] || '',
    poBox: parts[2] || '',
  };
};

const buildDescription = ({ address, city, poBox }) =>
  [address, city, poBox].filter(Boolean).join(', ');

const CreateCompany = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const isEditMode = Boolean(id);
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditMode);
  const [error, setError] = useState('');
  const [governanceTouched, setGovernanceTouched] = useState(false);

  const { items: companyTypes, loading: typesLoading } = useLookups('COMPANY_TYPE');
  const { items: countries, loading: countriesLoading } = useLookups('COUNTRY');
  const { items: entityClassifications, loading: classificationsLoading } = useLookups('ENTITY_CLASSIFICATION');

  const [formData, setFormData] = useState({
    name: '',
    tradeLicenseNumber: '',
    structureType: 'Subsidiary',
    governanceTypeCode: 'LS',
    parentCompanyId: '',
    shareholdingPercentage: '',
    address: '',
    poBox: '',
    city: '',
    country: 'UAE',
    phone: '',
    email: '',
    website: '',
    capital: '',
    foundedYear: '',
    employeeCount: '',
    ceoName: '',
    entityClassification: '',
    profileSummary: '',
    assignedCSId: '',
  });

  const [errors, setErrors] = useState({});
  const [parentCompanies, setParentCompanies] = useState([{ id: 1, name: 'Etisalat Group PJSC' }]);
  const [csPool, setCsPool] = useState([]);

  // Use only the lookup data without fallbacks
  const countryOptions = useMemo(() => {
    return [...countries];
  }, [countries]);

  const isCompanySecretary = isCompanySecretaryRole(user?.role);
  const isGroupAdmin = canCreateParentCompany(user?.role);
  const { allowed: canCreateCompanies, loading: permissionLoading } = useGlobalPermission('companies', 'create');

  useEffect(() => {
    if (!isEditMode && !permissionLoading && !canCreateCompanies) {
      navigate('/companies');
    }
  }, [isEditMode, navigate, canCreateCompanies, permissionLoading]);

  useEffect(() => {
    if (!isEditMode && isCompanySecretary && !isGroupAdmin) {
      setFormData((prev) => ({
        ...prev,
        structureType: 'Subsidiary',
      }));
    }
  }, [isEditMode, isCompanySecretary, isGroupAdmin]);

  const groupTypeOptions = useMemo(
    () => filterCompanyTypesByStructure(companyTypes, 'GROUP'),
    [companyTypes]
  );

  const subsidiaryTypeOptions = useMemo(
    () => filterCompanyTypesByStructure(companyTypes, 'SUBSIDIARY'),
    [companyTypes]
  );

  const governanceOptions = formData.structureType === 'Parent'
    ? groupTypeOptions
    : subsidiaryTypeOptions;

  const selectedGovernance = governanceOptions.find((t) => t.code === formData.governanceTypeCode)
    || companyTypes.find((t) => t.code === formData.governanceTypeCode);

  useEffect(() => {
    if (!isGroupAdmin) return;

    csAdminApi.getCompanySecretaries()
      .then((res) => {
        const list = (res.data?.data || [])
          .filter((secretary) => secretary.status !== 'INACTIVE' && secretary.status !== 'inactive')
          .map((secretary) => ({
            id: secretary.id,
            name: secretary.name,
            email: secretary.email,
          }));
        setCsPool(list);
      })
      .catch(() => setCsPool([]));
  }, [isGroupAdmin]);

  useEffect(() => {
    csAdminApi.getCompanies().then((res) => {
        const list = (res.data?.data || []).map((c) => ({
          id: c.id || c.company_id,
          name: c.name || c.company_name,
        }));
        if (list.length) {
          setParentCompanies(
            isEditMode ? list.filter((company) => String(company.id) !== String(id)) : list
          );
        }
    }).catch(() => {});
  }, [id, isEditMode]);

  useEffect(() => {
    if (!isEditMode) {
      setFetching(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setFetching(true);
        setError('');
        const res = await csAdminApi.getCompanyById(id);
        const mapped = mapCompanyFromApi(res.data?.data);
        if (!mapped) {
          if (!cancelled) setError('Company not found');
          return;
        }
        const contact = parseDescriptionFields(mapped.description);
        if (!cancelled) {
          setGovernanceTouched(true);
          setFormData({
            name: mapped.name || '',
            tradeLicenseNumber: mapped.tradeLicenseNumber || '',
            structureType: mapped.structureType || (mapped.parent_company_id ? 'Subsidiary' : 'Parent'),
            governanceTypeCode: mapLegacyGovernanceCode(mapped.company_type_code),
            parentCompanyId: mapped.parent_company_id ? String(mapped.parent_company_id) : '',
            shareholdingPercentage: mapped.shareholding_percentage ?? '',
            address: contact.address,
            poBox: contact.poBox,
            city: contact.city,
            country: mapped.country || 'UAE',
            phone: mapped.phone || '',
            email: mapped.email || '',
            website: mapped.website || '',
            capital: mapped.capital || mapped.revenue || '',
            foundedYear: mapped.foundedYear ?? '',
            employeeCount: mapped.employeeCount ?? '',
            ceoName: mapped.ceoName || '',
            entityClassification: mapped.entityClassification || '',
            profileSummary: mapped.profileSummary || '',
            assignedCSId: mapped.assignedCSId ? String(mapped.assignedCSId) : '',
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error?.message || 'Failed to load company');
        }
      } finally {
        if (!cancelled) setFetching(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, isEditMode]);

  useEffect(() => {
    if (governanceTouched || typesLoading) return;
    if (formData.structureType === 'Parent') {
      const defaultGroup = groupTypeOptions[0]?.code || 'E&G';
      setFormData((prev) => ({ ...prev, governanceTypeCode: defaultGroup }));
      return;
    }
    const suggested = suggestGovernanceType(formData.country, countries, companyTypes);
    setFormData((prev) => ({ ...prev, governanceTypeCode: suggested }));
  }, [formData.structureType, formData.country, countries, companyTypes, groupTypeOptions, governanceTouched, typesLoading]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'structureType') {
        setGovernanceTouched(false);
        if (value === 'Parent') {
          next.parentCompanyId = '';
          next.shareholdingPercentage = '';
          next.governanceTypeCode = groupTypeOptions[0]?.code || 'E&G';
        } else {
          next.governanceTypeCode = suggestGovernanceType(prev.country, countries, companyTypes);
        }
      }
      if (name === 'country' && prev.structureType === 'Subsidiary') {
        setGovernanceTouched(false);
        next.governanceTypeCode = suggestGovernanceType(value, countries, companyTypes);
      }
      if (name === 'governanceTypeCode') {
        setGovernanceTouched(true);
      }
      return next;
    });
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Company name is required';
    }

    if (!formData.tradeLicenseNumber.trim()) {
      newErrors.tradeLicenseNumber = 'Trade license number is required';
    }

    if (formData.structureType === 'Subsidiary' && !formData.parentCompanyId) {
      newErrors.parentCompanyId = 'Parent company is required for subsidiaries';
    }

    if (!formData.governanceTypeCode) {
      newErrors.governanceTypeCode = 'Company classification is required';
    }

    if (formData.email.trim() && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      enqueueSnackbar('Please fix the errors in the form', { variant: 'error' });
      return;
    }

    try {
      setLoading(true);
      setError('');

      const governanceLabel = selectedGovernance?.label
        || companyTypes.find((t) => t.code === formData.governanceTypeCode)?.label
        || formData.governanceTypeCode;

      {
        const payload = {
          company_name: formData.name,
          registration_number: formData.tradeLicenseNumber,
          country: formData.country,
          description: buildDescription(formData),
          parent_company_id: formData.structureType === 'Subsidiary' && formData.parentCompanyId
            ? Number(formData.parentCompanyId)
            : null,
          shareholding_percentage: formData.structureType === 'Subsidiary' && formData.shareholdingPercentage !== ''
            ? parseShareholdingPercent(formData.shareholdingPercentage)
            : null,
          company_type_code: formData.governanceTypeCode,
          company_type_name: governanceLabel,
          assignedCSId: formData.assignedCSId ? Number(formData.assignedCSId) : null,
          phone: formData.phone || null,
          email: formData.email || null,
          website: formData.website || null,
          capital: formData.capital || null,
          founded_year: formData.foundedYear !== '' ? Number(formData.foundedYear) : null,
          employee_count: formData.employeeCount !== '' ? Number(formData.employeeCount) : null,
          ceo_name: formData.ceoName || null,
          entity_classification: formData.entityClassification || null,
          profile_summary: formData.profileSummary || null,
        };

        if (isEditMode) {
          await csAdminApi.updateCompany(id, payload);
        } else {
          await csAdminApi.createCompany(payload);
        }
      }

      enqueueSnackbar(
        isEditMode ? 'Company updated successfully' : 'Company created successfully',
        { variant: 'success' }
      );
      if (isEditMode) {
        navigate(`/companies/${id}`);
      } else {
        navigate('/companies', { state: { refresh: Date.now() } });
      }
    } catch (err) {
      console.error(isEditMode ? 'Error updating company:' : 'Error creating company:', err);
      setError(err.response?.data?.message || err.response?.data?.error?.message
        || (isEditMode ? 'Failed to update company' : 'Failed to create company'));
      enqueueSnackbar(isEditMode ? 'Failed to update company' : 'Failed to create company', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (isEditMode) {
      navigate(`/companies/${id}`);
      return;
    }
    navigate('/companies');
  };

  if (fetching) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {isEditMode ? 'Edit Company' : 'Create New Company'}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {isEditMode
            ? 'Update company details in the organizational structure'
            : 'Add a new company entity to the organizational structure'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            Basic Information
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Company Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                error={!!errors.name}
                helperText={errors.name}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Trade License Number"
                name="tradeLicenseNumber"
                value={formData.tradeLicenseNumber}
                onChange={handleChange}
                error={!!errors.tradeLicenseNumber}
                helperText={errors.tradeLicenseNumber}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Organizational Structure</InputLabel>
                <Select
                  name="structureType"
                  value={formData.structureType}
                  onChange={handleChange}
                  disabled={loading || (isCompanySecretary && !isGroupAdmin && !isEditMode)}
                  label="Organizational Structure"
                >
                  {isGroupAdmin && (
                    <MenuItem value="Parent">Parent / Group Company</MenuItem>
                  )}
                  <MenuItem value="Subsidiary">Subsidiary</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required error={!!errors.governanceTypeCode}>
                <InputLabel>Company Classification</InputLabel>
                <Select
                  name="governanceTypeCode"
                  value={formData.governanceTypeCode}
                  onChange={handleChange}
                  disabled={loading || typesLoading}
                  label="Company Classification"
                >
                  {governanceOptions.map((type) => (
                    <MenuItem key={type.code} value={type.code}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {formData.structureType === 'Subsidiary' && (
                <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                  Suggested from country — confirm Local (LS), Foreign (FS), or ADGM as applicable.
                </Typography>
              )}
            </Grid>

            {formData.structureType === 'Subsidiary' && (
              <>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required error={!!errors.parentCompanyId}>
                    <InputLabel>Parent Company</InputLabel>
                    <Select
                      name="parentCompanyId"
                      value={formData.parentCompanyId}
                      onChange={handleChange}
                      disabled={loading}
                      label="Parent Company"
                    >
                      {parentCompanies.map((company) => (
                        <MenuItem key={company.id} value={company.id}>
                          {company.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Shareholding Percentage"
                    name="shareholdingPercentage"
                    value={formData.shareholdingPercentage}
                    onChange={handleChange}
                    disabled={loading}
                    inputProps={shareholdingPercentInputProps}
                    InputProps={{ endAdornment: '%' }}
                  />
                </Grid>
              </>
            )}
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            Contact Information
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="P.O. Box"
                name="poBox"
                value={formData.poBox}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="City"
                name="city"
                value={formData.city}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Country</InputLabel>
                <Select
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  disabled={loading || countriesLoading}
                  label="Country"
                >
                  {countryOptions.map((c) => (
                    <MenuItem key={c.code} value={c.code}>
                      {c.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="email"
                label="Email (optional)"
                name="email"
                value={formData.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email || 'Company contact email — not required'}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Website"
                name="website"
                value={formData.website}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            Corporate Profile
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Capital"
                name="capital"
                value={formData.capital}
                onChange={handleChange}
                placeholder="e.g. AED 50M"
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="entity-classification-label">Entity Classification</InputLabel>
                <Select
                  labelId="entity-classification-label"
                  id="entity-classification"
                  name="entityClassification"
                  value={formData.entityClassification}
                  onChange={handleChange}
                  label="Entity Classification"
                  disabled={loading || classificationsLoading}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {entityClassifications.map((classification) => (
                    <MenuItem key={classification.code} value={classification.code}>
                      {classification.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Year Founded"
                name="foundedYear"
                type="number"
                value={formData.foundedYear}
                onChange={handleChange}
                disabled={loading}
                inputProps={{ min: 1800, max: new Date().getFullYear() }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Employees"
                name="employeeCount"
                type="number"
                value={formData.employeeCount}
                onChange={handleChange}
                disabled={loading}
                inputProps={{ min: 0 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="CEO"
                name="ceoName"
                value={formData.ceoName}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Company Profile Summary"
                name="profileSummary"
                value={formData.profileSummary}
                onChange={handleChange}
                disabled={loading}
                helperText="Shown in org structure and company overview"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          {isGroupAdmin && (
            <>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            Company Secretary Assignment (Optional)
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Autocomplete
                options={csPool}
                getOptionLabel={(option) => `${option.name} (${option.email})`}
                value={csPool.find((cs) => String(cs.id) === String(formData.assignedCSId)) || null}
                onChange={(event, newValue) => {
                  setFormData((prev) => ({
                    ...prev,
                    assignedCSId: newValue?.id ? String(newValue.id) : '',
                  }));
                }}
                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                disabled={loading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assign Company Secretary"
                    helperText={
                      csPool.length
                        ? 'Select a company secretary from the pool (can be assigned later)'
                        : 'No company secretaries available — create one from Company Secretaries first'
                    }
                  />
                )}
              />
            </Grid>
          </Grid>
            </>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              disabled={loading}
            >
              {isEditMode ? 'Save Changes' : 'Create Company'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default CreateCompany;
